import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useUpdateLivretCompetence } from '@teetsh/app/src/containers/Livrets/queries/competences/useUpdateLivretCompetence';
import { useUpdateLivretEleve } from '@teetsh/app/src/containers/Livrets/queries/useUpdateLivretEleve';
import { useUpdateLivretEleveMatiere } from '@teetsh/app/src/containers/Livrets/queries/useUpdateLivretEleveMatiere';
import { useUpdateLivretEval } from '@teetsh/app/src/containers/Livrets/queries/useUpdateLivretEval';
import {
  LivretEleve,
  LivretEval,
  Periode,
} from '@teetsh/app/src/containers/Livrets/types/livret';
import useUpdateSchoolDebounce from '@teetsh/app/src/queries/schools/useUpdateSchoolDebounce';
import { Eleve } from '@teetsh/app/src/types/eleves';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';
import 'twin.macro';
import { Bo } from 'types/bo';
import { Contact } from 'types/school';
import { CompetencesNumeriqueList } from './components/LivretEleve/CompetencesNumeriques';
import { FamilyCom } from './components/LivretEleve/FamilyCom';
import { GlobalRemark } from './components/LivretEleve/GlobalRemark';
import { LivretHeader } from './components/LivretEleve/LivretHeader';
import { LivretStructure } from './components/LivretEleve/LivretStructure';
import { LivretTable } from './components/LivretEleve/LivretTable';
import { ParcoursList } from './components/LivretEleve/ParcoursList';
import { SchoolInfo } from './components/LivretEleve/SchoolInfo';
import { SocleCommunList } from './components/LivretEleve/SocleCommun';
import { LivretEleveHeader } from './Header';
import React from 'react';
import { GlobalRemarkTitleWrapper } from './components/LivretEleve/GlobalRemark/GlobalRemarkTitleWrapper';

interface Props {
  livretEleve: LivretEleve;
  livretEval: LivretEval;
  eleve: Eleve;
  periodes: Periode[];
  directors: Contact[];
  bo: Bo;
  loadingDirectors: boolean;
  backLink?: string;
  readonly?: boolean;
  withIA: boolean;
}

export function LivretEleveRoot(props: Props) {
  const {
    livretEleve,
    livretEval,
    eleve,
    periodes,
    directors,
    bo,
    loadingDirectors,
    readonly,
  } = props;

  const periode = periodes.find(per => per.id === livretEval.periodeId);
  const classrooms = useClassrooms();
  const classroom = classrooms.find(c => c.id === livretEval.classroomId);
  const user = useUser();
  const { schoolyear, school } = useSchoolSchoolyear();
  const updateLivretEleve = useUpdateLivretEleve({
    livretEvalId: livretEval.id,
    livretEleveId: livretEleve.id,
  });
  const updateLivretEval = useUpdateLivretEval();
  const updateMatiere = useUpdateLivretEleveMatiere({
    livretEvalId: livretEval.id,
    livretEleveId: livretEleve.id,
  });
  const updateCompetence = useUpdateLivretCompetence({
    livretEleveId: livretEleve.id,
    livretEvalId: livretEval.id,
  });

  const [schoolDebounced, setSchool] = useUpdateSchoolDebounce(
    school,
    schoolyear.id,
  );
  if (!classroom || !periode) {
    return null;
  }
  const classroomCycle = classroom.cycle[0];
  const boMatieres =
    bo.cycles.find(c => c.nom === classroomCycle)?.matieres || [];
  const parcoursList = livretEval.parcoursList || [];
  // Le key là est import pour que le component soit bien rechargé
  // Sinon tu récupères les données de l'élève juste avant...
  return (
    <div key={eleve.id}>
      <div tw="mb-4">
        <LivretEleveHeader
          readonly={readonly}
          livretEval={livretEval}
          livretEleve={livretEleve}
          eleve={eleve}
          backLink={props.backLink}
        />
      </div>
      <LivretStructure>
        <LivretStructure.Header>
          <SchoolInfo
            readonly={user.isNotLoggedIn || readonly}
            school={schoolDebounced}
            onUpdateSchoolField={(field, value) =>
              setSchool({
                ...schoolDebounced,
                [field]: value,
              })
            }
          />
          <LivretHeader
            eleveBirthday={eleve.birthday}
            classroomName={classroom.name}
            eleveGivenName={eleve.givenName}
            eleveFamilyName={eleve.familyName}
            classroomCycle={classroomCycle}
            livretName={livretEval.name}
            schoolyearLabel={schoolyear.label}
            periodeFrom={periode.from}
            periodeTo={periode.to}
          />
        </LivretStructure.Header>
        <LivretStructure.SuiviAcquis>
          <div tw="overflow-x-auto">
            <LivretTable
              livretEleve={livretEleve}
              livretEvalId={livretEval.id}
              typePositionnement={livretEval.typePositionnement}
              notationSystem={livretEval.notationSystem}
              onUpdateMatiere={readonly ? undefined : updateMatiere}
              onUpdateCompetence={readonly ? undefined : updateCompetence}
              boMatieres={boMatieres}
              withIA={props.withIA}
            />
          </div>
          <div tw="mt-8">
            <ParcoursList
              value={parcoursList}
              readonly={readonly}
              onChange={
                readonly
                  ? undefined
                  : v =>
                      updateLivretEval({
                        parcoursList: v,
                        id: livretEval.id,
                        name: livretEval.name,
                        notationSystem: livretEval.notationSystem,
                        carnetIds: livretEval.carnetIds,
                        periodeId: periode.id,
                        typePositionnement: livretEval.typePositionnement,
                        additionalTeachers: livretEval.additionalTeachers,
                      })
              }
            />
            {classroomCycle === 'Cycle 3' && (
              <CompetencesNumeriqueList
                tw="mt-8"
                livretEleve={livretEleve}
                readonly={readonly}
                livretEvalId={livretEval.id}
                onChange={readonly ? undefined : updateLivretEleve}
              />
            )}
            <SocleCommunList
              tw="mt-8"
              readonly={readonly}
              livretEvalId={livretEval.id}
              livretEleve={livretEleve}
              onChange={readonly ? undefined : updateLivretEleve}
            />
          </div>
        </LivretStructure.SuiviAcquis>
        <LivretStructure.Bilan>
          <GlobalRemarkTitleWrapper>
            <GlobalRemark
              withIA={props.withIA}
              livretEleveId={livretEleve.id}
              readonly={readonly}
              value={livretEleve.bilan || ''}
              onChange={bilan => {
                updateLivretEleve({ id: livretEleve.id, bilan });
              }}
            />
          </GlobalRemarkTitleWrapper>
        </LivretStructure.Bilan>
        <LivretStructure.Family>
          <FamilyCom
            onUpdateAdditionalTeachers={
              readonly
                ? undefined
                : additionalTeachers =>
                    updateLivretEval({
                      id: livretEval.id,
                      name: livretEval.name,
                      typePositionnement: livretEval.typePositionnement,
                      notationSystem: livretEval.notationSystem,
                      carnetIds: livretEval.carnetIds,
                      periodeId: periode.id,
                      additionalTeachers,
                    })
            }
            additionalTeachers={livretEval.additionalTeachers}
            loading={loadingDirectors}
            director={
              directors?.[0]?.familyName && directors[0].givenName
                ? directors[0]
                : undefined
            }
            user={{
              familyName: user.familyName,
              givenName: user.givenName,
              gender: user.gender,
            }}
          />
        </LivretStructure.Family>
      </LivretStructure>
    </div>
  );
}
